import {SC_Permissions} from "./SC_Permissions";
import {SC_Department} from "./enums/SC_Department";

/**
 * Navigation item model for the responsive navigation menu system.
 * Extensible configuration-based approach for permission-aware navigation.
 */
export class SC_NavigationItem {
    /**
     * Input property name constants for fromAPI
     */
    static readonly inputKeys = {
        title: "title",
        path: "path",
        icon: "icon",
        permission: "permission",
        group: "group",
        subItems: "subItems",
        isActive: "isActive",
        departments: "departments",
    };

    /**
     * Available permission flags that can be used for gating menu items.
     * Uses SC_Permissions.inputKeys constants for type safety.
     */
    static readonly AVAILABLE_PERMISSIONS: (keyof typeof SC_Permissions.inputKeys)[] = [
        SC_Permissions.inputKeys.scheduleService,
        SC_Permissions.inputKeys.editService,
        SC_Permissions.inputKeys.viewLocations,
        SC_Permissions.inputKeys.writeLocations,
        SC_Permissions.inputKeys.writeCrews,
        SC_Permissions.inputKeys.viewReports,
        SC_Permissions.inputKeys.writeReports,
        SC_Permissions.inputKeys.viewSubs,
        SC_Permissions.inputKeys.writeSubs,
        SC_Permissions.inputKeys.viewBudget,
        SC_Permissions.inputKeys.writeBudget,
        SC_Permissions.inputKeys.viewAdvancedPayroll,
        SC_Permissions.inputKeys.writeAdvancedPayroll,
        SC_Permissions.inputKeys.viewEmployeeReports,
        SC_Permissions.inputKeys.writeEmployeeReports,
        SC_Permissions.inputKeys.atLeastCoOwn,
        SC_Permissions.inputKeys.seeNonOwnedData,
        SC_Permissions.inputKeys.seeCoOwnedData,
    ];

    /**
     * Default permission for items requiring scheduleService (most work pages).
     */
    static readonly DEFAULT_WORK_PERMISSION = SC_Permissions.inputKeys.scheduleService;

    /**
     * Maps permission input keys to SC_Permissions property names.
     * Used by matchesPermission() to look up the correct property.
     */
    private static readonly PERMISSION_MAP: Record<string, keyof SC_Permissions> = {
        [SC_Permissions.inputKeys.scheduleService]: "scheduleService",
        [SC_Permissions.inputKeys.editService]: "editService",
        [SC_Permissions.inputKeys.viewLocations]: "viewLocations",
        [SC_Permissions.inputKeys.writeLocations]: "writeLocations",
        [SC_Permissions.inputKeys.writeCrews]: "writeCrews",
        [SC_Permissions.inputKeys.viewReports]: "viewReports",
        [SC_Permissions.inputKeys.writeReports]: "writeReports",
        [SC_Permissions.inputKeys.viewSubs]: "viewSubs",
        [SC_Permissions.inputKeys.writeSubs]: "writeSubs",
        [SC_Permissions.inputKeys.viewBudget]: "viewBudget",
        [SC_Permissions.inputKeys.writeBudget]: "writeBudget",
        [SC_Permissions.inputKeys.viewAdvancedPayroll]: "viewAdvancedPayroll",
        [SC_Permissions.inputKeys.writeAdvancedPayroll]: "writeAdvancedPayroll",
        [SC_Permissions.inputKeys.viewEmployeeReports]: "viewEmployeeReports",
        [SC_Permissions.inputKeys.writeEmployeeReports]: "writeEmployeeReports",
        [SC_Permissions.inputKeys.atLeastCoOwn]: "atLeastCoOwn",
        [SC_Permissions.inputKeys.seeNonOwnedData]: "seeNonOwnedData",
        [SC_Permissions.inputKeys.seeCoOwnedData]: "seeCoOwnedData",
    };

    constructor({
        title = "",
        path = "",
        icon = "fa-question",
        permission = SC_NavigationItem.DEFAULT_WORK_PERMISSION,
        group = "Uncategorized",
        subItems = [],
        isActive = false,
        departments = [],
    } = {}) {
        this.title = title;
        this.path = path;
        this.icon = icon;
        this.permission = permission;
        this.group = group;
        this.subItems = subItems;
        this.isActive = isActive;
        this.departments = departments;
    }

    title: string;
    path: string;
    icon: string;
    permission: string;
    group: string;
    subItems: SC_NavigationItem[];
    isActive: boolean;

    /**
     * Array of departments this navigation item applies to.
     * Used for filtering navigation based on user's department access.
     */
    departments: SC_Department[];

    /**
     * Creates a navigation item from API response.
     * @param jsonin - JSON object from backend
     * @returns SC_NavigationItem instance
     */
    static fromAPI(jsonin: Record<string, any>): SC_NavigationItem {
        return new SC_NavigationItem({
            title: jsonin[SC_NavigationItem.inputKeys.title] ?? "",
            path: jsonin[SC_NavigationItem.inputKeys.path] ?? "",
            icon: jsonin[SC_NavigationItem.inputKeys.icon] ?? "fa-question",
            permission: jsonin[SC_NavigationItem.inputKeys.permission] ?? SC_NavigationItem.DEFAULT_WORK_PERMISSION,
            group: jsonin[SC_NavigationItem.inputKeys.group] ?? "Uncategorized",
            subItems: (jsonin[SC_NavigationItem.inputKeys.subItems] ?? []).map((item: any) => SC_NavigationItem.fromAPI(item)),
            isActive: jsonin[SC_NavigationItem.inputKeys.isActive] ?? false,
            departments: (jsonin[SC_NavigationItem.inputKeys.departments] ?? []).map((dept: any) => SC_Department.fromAPI(dept)),
        });
    }

    /**
     * Converts navigation item to API format.
     * @returns Record<string, any>
     */
    toAPI(): Record<string, any> {
        return {
            [SC_NavigationItem.inputKeys.title]: this.title,
            [SC_NavigationItem.inputKeys.path]: this.path,
            [SC_NavigationItem.inputKeys.icon]: this.icon,
            [SC_NavigationItem.inputKeys.permission]: this.permission,
            [SC_NavigationItem.inputKeys.group]: this.group,
            [SC_NavigationItem.inputKeys.subItems]: this.subItems.map((item) => item.toAPI()),
            [SC_NavigationItem.inputKeys.isActive]: this.isActive,
            [SC_NavigationItem.inputKeys.departments]: this.departments.map((dept) => dept.toAPI()),
        };
    }

    /**
     * Creates a deep clone of this navigation item.
     * @returns New SC_NavigationItem instance
     */
    clone(): SC_NavigationItem {
        const cloned = new SC_NavigationItem();
        cloned.title = this.title;
        cloned.path = this.path;
        cloned.icon = this.icon;
        cloned.permission = this.permission;
        cloned.group = this.group;
        cloned.subItems = this.subItems.map((item) => item.clone());
        cloned.isActive = this.isActive;
        cloned.departments = [...this.departments];
        return cloned;
    }

    /**
     * Checks if the given permissions object grants access to this menu item.
     * @param perms - SC_Permissions instance
     * @returns true if user has required permission, false otherwise
     */
    matchesPermission(perms: SC_Permissions): boolean {
        if (!this.permission) return true;

        const prop = SC_NavigationItem.PERMISSION_MAP[this.permission];
        // console.log("Failed perm", this, prop);
        if (!prop) return false;

        return (perms as any)[prop] === true;
    }

    /**
     * Gets the FontAwesome icon class for this item.
     * @returns FontAwesome icon class string
     */
    getIconClass(): string {
        return `fa-${this.icon}`;
    }

    /**
     * Gets the display label for the menu item (title or fallback).
     * @returns Display label string
     */
    getDisplayLabel(): string {
        return this.title || "Untitled";
    }

    matchesFilter(perms: SC_Permissions, departments: SC_Department[]) {
        if (this.departments?.length) {
            if (!departments?.length)
                return false;

            if (this.departments.some(dept => !departments.some(it => it.toAPI() === dept.toAPI()))) {
                return false;
            }
        }

        return this.matchesPermission(perms);
    }
}
